import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  aceitarConvitePendente,
  fetchConvitesPendentes,
  recusarConvitePendente,
  type ConvitePendente,
} from '@/api/client';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

/** Os convites que a pessoa recebeu e ainda não respondeu (o aviso do painel e de Escritórios). */
export function useConvitesPendentes(enabled = true) {
  return useQuery<ConvitePendente[]>({
    queryKey: [...queryKeys.escritorios.pendentes],
    queryFn: fetchConvitesPendentes,
    enabled,
    staleTime: 60 * 1000,
  });
}

/** Depois do aceite (pelo link ou pelo aviso): abre o escritório na aba de compartilhamento. */
export function useEntrarNoEscritorio() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();
  return useCallback(
    (res: { escritorio_id: string; nome: string }) => {
      showSuccess(`Convite aceito: você agora é membro de "${res.nome}"`);
      queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.all });
      // A vaga paga pelo escritório (§29) chega pela assinatura: sem isto, o gate seguia com a
      // resposta guardada (sem a vaga) e mandava ao paywall por até 5 min.
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      navigate(`/app/escritorios/${res.escritorio_id}?aba=compartilhamento&bemvindo=1`, { replace: true });
    },
    [navigate, queryClient, showSuccess],
  );
}

export function useResponderConvite() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const entrar = useEntrarNoEscritorio();
  const comErro = { onError: (err: unknown) => notifyMutationError(err, showError) };
  return {
    aceitar: useMutation({ mutationFn: aceitarConvitePendente, onSuccess: entrar, ...comErro }),
    recusar: useMutation({
      mutationFn: (convite: ConvitePendente) => recusarConvitePendente(convite.id),
      onSuccess: (_r, convite) => {
        showSuccess(`Convite de "${convite.escritorio_nome}" recusado`);
        queryClient.invalidateQueries({ queryKey: [...queryKeys.escritorios.pendentes] });
      },
      ...comErro,
    }),
  };
}
